import { useEffect } from "react";
import {
    EditFormContents,
    EditFormElement,
    SaveEditFormResponse,
    useEditForm,
} from "@kaltura/mediaspace-shared-edit-entry";
import { Button, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { getComponentFromPath, translate } from "@kaltura/mediaspace-shared-utils";
import { StyledModal, StyledModalActions } from "../../ModalStyledComponents";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { FormProvider } from "react-hook-form";

export interface BulkEditModalProps {
    entryIds: string[];
    fields: EditFormElement[];
    onBeforeSubmit?: (entryIds: string[], data: any) => Promise<{ confirm: boolean; isEditable: boolean }>;
    onReadyForPublish?: (entryIds: string[], readyForPublish: boolean) => void;
    onSubmit: (response: SaveEditFormResponse) => void;
    onCancel: () => void;
    open: boolean;
    components?: KmsTypeComponent[];
}

function BulkEditModal({
    entryIds,
    fields,
    onReadyForPublish,
    onBeforeSubmit,
    onSubmit,
    onCancel,
    open,
    components,
}: BulkEditModalProps) {
    const filteredFields = fields.filter((field) => field.showOn === "bulk" || field.showOn === "both");

    const editFormHooksProps = useEditForm({
        entryIds,
        formData: {},
        fieldsStructure: filteredFields,
        onSubmit,
        onClose: onCancel,
        onBeforeSubmit,
    });

    const handleReadyForPublish = useEventHandler(onReadyForPublish);

    useEffect(() => {
        // report readyForPublish when it changes, or the entryIds change (entry added or removed)
        // not using entryIds array as dependency - it changes every render
        handleReadyForPublish?.(entryIds, editFormHooksProps.readyForPublish);
    }, [handleReadyForPublish, entryIds.length, editFormHooksProps.readyForPublish]);

    return (
        <StyledModal
            variant="task"
            open={open}
            classes={{ paper: "upload-modal uploadBulkEditModal" }}
            className={"kms-ds-upload-bulk-edit-modal"}
        >
            <ModalTitle>{translate("Edit All")}</ModalTitle>
            <ModalContent>
                <form id={editFormHooksProps.formId} onSubmit={editFormHooksProps.handleSubmitForm}>
                    <EditFormContents formData={{}} fieldsStructure={filteredFields} {...editFormHooksProps} />
                    <FormProvider {...editFormHooksProps.useFormHookMethods}>
                        {components?.map((component: KmsTypeComponent, index: number) => {
                            const Component = getComponentFromPath(component.component);

                            if (!Component) {
                                return null;
                            }

                            const { fieldsStructure, ...componentProps } = component.props;

                            return (
                                <ErrorBoundary fallback={<></>} key={index}>
                                    <Component {...componentProps} entryIds={entryIds}>
                                        <EditFormContents
                                            {...editFormHooksProps}
                                            formData={{}}
                                            fieldsStructure={fieldsStructure}
                                        />
                                    </Component>
                                </ErrorBoundary>
                            );
                        })}
                    </FormProvider>
                </form>
            </ModalContent>
            <StyledModalActions>
                <Button
                    variant={"borderless"}
                    color={"secondary"}
                    onClick={editFormHooksProps.handleCancel}
                    disabled={editFormHooksProps.processing}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    type={"submit"}
                    form={editFormHooksProps.formId}
                    disabled={!editFormHooksProps.canSaveForm}
                    loading={editFormHooksProps.processing}
                >
                    {translate("Update All")}
                </Button>
            </StyledModalActions>
        </StyledModal>
    );
}

export default BulkEditModal;
